import Pane from 'components/Common/Pane'
import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import AlwaysOnRateOption from './AlwaysOnRateOption'
import HighlightMessage from 'components/Common/HighlightMessage/HighlightMessage'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { calendarAction, urgencyLabelImpression } from 'analytics/eventDefinitions'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OFFER_TYPE_ALWAYS_ON_ANALYTICS } from 'constants/offer'
import { useInView } from 'react-intersection-observer'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import InlineGroup from 'components/Common/InlineGroup/InlineGroup'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  packageViews: Array<OfferPackageOptionView>;
  checkIn: moment.Moment;
  rooms: Array<App.Occupants>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  isHighlighted?: boolean;
  onViewPackageOptions: () => void;
  roomRateId?: string;
  bookedToday?: number;
  queryParam?: Record<string, string>;
}

const getInitialRatesToShow = (isHighlighted: boolean) => isHighlighted ? 1 : 3

function AlwaysOnRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    checkIn,
    rooms,
    flightDestination,
    previewMode,
    isHighlighted,
    onViewPackageOptions,
    roomRateId,
    bookedToday,
    queryParam,
  } = props

  const { utm_campaign } = queryParam ?? {}

  const pkg = packageViews[0].package
  const [showingMoreRates, showMoreRates] = useToggle()
  const [isRoomRateUnmatched, setIsRoomRateUnmatched] = useState(false)
  const offerPageDispatch = useOfferPageDispatch()
  const initialRatesToShow = useMemo(() => getInitialRatesToShow(!!isHighlighted), [isHighlighted])

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])
  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  const availableForPurchase = useIsAvailableForPurchase()

  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const options = previewMode ? packageViews : availablePackageViews

  const rateOptionList = useMemo(() => {
    if (isHighlighted && roomRateId && options.length >= 1) {
      /**
        * Modifying the rateOption array so we have the highlightedRateOption on top
       **/
      const option = options.find((view) => view.package.roomRate?.id === roomRateId)
      if (option) {
        return [option, ...options.filter((view) => view.package.roomRate?.id !== roomRateId)]
      } else if (utm_campaign == 'google-hotel-ads') {
        setIsRoomRateUnmatched(true)
      }
    }
    return options
  }, [isHighlighted, roomRateId, options, utm_campaign])

  const { ref: urgencyLabelsDesktopRef, inView: urgencyLabelsInDesktopView } = useInView({ triggerOnce: true })

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const urgencyLabelsInView = urgencyLabelsInDesktopView
  useEffect(() => {
    if (urgencyLabelsInView) {
      fireNonInteractionEvent(urgencyLabelImpression())
    }
  }, [urgencyLabelsInView])

  const bookedTodayLabel = (bookedToday ?? 0) >= 3 ? (
    <LabelGroup>
      <UrgencyLabel variant="critical1" ref={urgencyLabelsDesktopRef}>
        <InlineGroup vertical="center" spacing="xs">
          <SolidBoltAltIcon size="XS" />
          Booked {bookedToday} times today
        </InlineGroup>
      </UrgencyLabel>
    </LabelGroup>
  ) : undefined

  const checkAvailability = !checkIn || !rooms

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      if (!offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return 'available-on-dates'
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (offer.property?.useDynamicOccupancy) {
        if (!checkIn) return 'available-on-dates'
        if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
      }
    }
  }, [availableForPurchase, checkAvailability, checkIn, datesAvailable, offer, roomFitsCapacity])

  const toggleFilter = useCallback((filter: OfferPageFilterTypes) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    fireInteractionEvent(calendarAction(
      OFFER_TYPE_ALWAYS_ON_ANALYTICS,
      CALENDAR_GA_EVENT_ACTION.OpenFilter,
      CALENDAR_GA_EVENT_LABEL.PackageOptionSoldOut))
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    fireInteractionEvent(calendarAction(
      OFFER_TYPE_ALWAYS_ON_ANALYTICS,
      CALENDAR_GA_EVENT_ACTION.OpenFilter,
      CALENDAR_GA_EVENT_LABEL.PackageOptionAvailability))
    toggleFilter(rooms ? 'checkin' : 'capacity')
  }, [rooms, toggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  if (!pkg.roomType) {
    return null
  }

  return (
    <Pane bordered data-testid="package-option">
      {isHighlighted && <HighlightMessage message="You were interested in this option" hideIcon={isRoomRateUnmatched}/>}
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile>
        <AccommodationRoomTileImage
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
          urgencyLabels={bookedTodayLabel}
          actions={availableForPurchase && roomFitsCapacity && datesAvailable && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        >
          <AccommodationRoomTileActions
            // @ts-expect-error not HotelPackage
            roomPackage={pkg}
            state={actionsState}
            onAddAnotherUnitClick={handleAddAnotherUnitClick}
            onChangeDatesClick={handleChangeDatesClick}
            onCheckAvailabilityClick={handleCheckAvailabilityClick}
          />
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection data-testid="rate-options">
        {take(rateOptionList, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <AlwaysOnRateOption
          key={view.package.uniqueKey}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          flightDestination={flightDestination}
        />)}
        {rateOptionList.length > initialRatesToShow &&
          <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
              {showingMoreRates ? 'Show less options' : 'Show more options'}
            </SmallToggleButton>
          </ShowMoreContent>
        }
      </RateSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState: App.State) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  previewMode: appState.config.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  queryParam: appState.router.location?.query,
}))(AlwaysOnRoomOption)
